import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { BadgerAuthScreen } from "./components/auth/BadgerAuthScreen.tsx";
import { BadgerAuthShell } from "./components/auth/BadgerAuthShell.tsx";
import { AuthProvider } from "./context/AuthContext.tsx";
import { msalInstance } from "./config/msalConfig.ts";
import "./assets/css/index.css";
import { appRouter } from "./router.tsx";

async function renderApp() {
  // Check if MSAL is enabled
  const isMsalEnabled = import.meta.env.VITE_MSAL_ENABLED === "true";

  if (!isMsalEnabled) {
    createRoot(document.getElementById("root")!).render(
      <AuthProvider>
        <RouterProvider router={appRouter} />
      </AuthProvider>,
    );
    return;
  }

  // MSAL is enabled, proceed with authentication
  if (!msalInstance) {
    createRoot(document.getElementById("root")!).render(
      <BadgerAuthShell>
        <BadgerAuthScreen
          variant="msal-config"
          error="MSAL is enabled but not properly configured."
        />
      </BadgerAuthShell>,
    );
    return;
  }

  try {
    await msalInstance.initialize();

    // Check if we're returning from a redirect
    // const urlParams = new URLSearchParams(window.location.search);
    // const hasAuthParams = urlParams.has('code') || urlParams.has('error') || urlParams.has('state');

    // if (hasAuthParams) {
    //   console.log('Detected auth parameters in URL, handling redirect...');
    //   console.log('URL params:', Object.fromEntries(urlParams.entries()));
    // }

    // Handle the redirect login response and set active account
    const authResult = await msalInstance.handleRedirectPromise();
    // console.log("Auth Result:", authResult);

    if (authResult && authResult.account) {
      // console.log('Setting active account from auth result:', authResult.account.username);
      msalInstance.setActiveAccount(authResult.account);
    } else if (authResult && "error" in authResult) {
      // Handle error case - this is a redirect error response
      const errorResult = authResult as any; // Type assertion for error handling
      console.error(
        "Auth result contains error:",
        errorResult.error,
        errorResult.errorDescription,
      );

      // Check for the specific cross-origin token redemption error
      if (
        errorResult.error === "invalid_request" &&
        errorResult.errorDescription?.includes("AADSTS9002326")
      ) {
        throw new Error(
          `Cross-origin token redemption error. Please ensure your Azure AD app is configured as a Single-Page Application with the correct redirect URI: ${window.location.origin}`,
        );
      }

      throw new Error(
        `Authentication failed: ${errorResult.errorDescription || errorResult.error}`,
      );
    } else {
      const currentAccounts = msalInstance.getAllAccounts();

      if (currentAccounts.length > 0) {
        msalInstance.setActiveAccount(currentAccounts[0]);
      }
    }

    createRoot(document.getElementById("root")!).render(
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <RouterProvider router={appRouter} />
        </AuthProvider>
      </MsalProvider>,
    );
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";

    createRoot(document.getElementById("root")!).render(
      <BadgerAuthShell>
        <BadgerAuthScreen
          variant="error"
          title="Sign-in failed"
          message="We could not initialize Microsoft authentication."
          error={errorMessage}
          debugLines={[
            "Check the browser console for detailed logs.",
            `Current URL: ${window.location.href}`,
            `VITE_MSAL_ENABLED: ${import.meta.env.VITE_MSAL_ENABLED}`,
            `VITE_FRONTEND_APP_ID: ${import.meta.env.VITE_FRONTEND_APP_ID ? "Set" : "Missing"}`,
            `VITE_TENANT_ID: ${import.meta.env.VITE_TENANT_ID ? "Set" : "Missing"}`,
            `VITE_REDIRECT_URI: ${import.meta.env.VITE_REDIRECT_URI ? "Set" : "Missing"}`,
          ]}
        />
      </BadgerAuthShell>,
    );
  }
}

renderApp();
